import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Upload, Trash2, Activity, Cpu, Save, Zap, ChevronRight, Menu, X as CloseIcon, BookOpen, Layers, Terminal } from 'lucide-react';
import { api } from '../lib/api';
import { OfflineAI } from '../lib/offlineAI';
import { useAuth } from '../contexts/AuthContext';
import MarkdownMessage from '../components/MarkdownMessage';
import GoogleSignInButton from '../components/GoogleSignInButton';
import SEO from '../components/SEO';

const quickStarters = [
  'My Arduino is not being recognized by my computer',
  'ESP32 keeps crashing with brownout error',
  'Capacitor on my PCB is leaking — what do I do?',
  'LED driver circuit getting too hot',
  'How do I read a MOSFET datasheet?',
  'Motor driver L298N not spinning the motor',
];

export default function Diagnose() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [isOfflineMode, setIsOfflineMode] = useState(!navigator.onLine);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAR, setShowAR] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { user, isLimitReached, incrementQuestions, login } = useAuth();

  useEffect(() => {
    const handleOnline = () => setIsOfflineMode(false);
    const handleOffline = () => setIsOfflineMode(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      setTimeout(() => {
        latestMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  useEffect(() => {
    if (loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleSend = async (text: string = input) => {
    if (!text.trim() || loading) return;
    if (isLimitReached) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    if (isOfflineMode || !navigator.onLine) {
      setLoadingStatus('Running Edge Inference (Offline mode)...');
      try {
        const response = await OfflineAI.getInstance().query(text);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response,
          confidence_score: 0.9,
          evidence_context: []
        }]);
      } catch (err) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "### Offline Model Unavailable\nYou are currently offline and no edge model is cached. Please go online to download the 'Technical Edge Pack' for offline reasoning."
        }]);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoadingStatus('Initializing ElectrAI diagnostic engine...');

    try {
      // Simulate progress updates for better UX
      const statusTimer = setInterval(() => {
        setLoadingStatus(prev => {
          if (prev.includes('engine')) return 'Consulting specialized electronics database...';
          if (prev.includes('database')) return 'Researching circuit diagrams & technical specs...';
          if (prev.includes('diagrams')) return 'Synthesizing expert repair procedure...';
          return prev;
        });
      }, 5000);

      const sanitizedHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const data = await api.chat(text, {}, sanitizedHistory);
      clearInterval(statusTimer);
      setLoadingStatus('');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || data.message || 'Processing complete.',
        evidence_context: data.evidence_context || [],
        confidence_score: data.confidence_score || 0,
        technical_data: data.technical_data || null,
        diagrams: data.diagrams || []
      }]);
      incrementQuestions();
    } catch (error: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `### Connection Error\nBackend unreachable: ${error.message || 'Unknown error'}. Check your network or API status.`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: `[Uploading image: ${file.name}...]` }]);
    try {
      const data = await api.analyzeImage(file, 'Identify components or diagnose issue in this electronics image');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Visual Analysis Result:**\n\n${data.summary || 'Image analyzed successfully.'}`
      }]);
    } catch (e: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Upload failed: ${e.message}` }]);
    } finally {
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.initialQuery) {
      handleSend(location.state.initialQuery);
      // Clear state so it doesn't run again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', background: 'var(--bg-main)' }}>
      <SEO 
        title="AI Circuit Diagnosis | DIYElectronicMech" 
        description="Chat with ElectrAI to troubleshoot your electronics projects. Get step-by-step repair guidance, component identification, and fault isolation."
      />
      
      {/* Main Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header bar */}
        <div style={{
          background: 'rgba(5,9,18,0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,255,136,0.15)',
          padding: '0.75rem 1.5rem',
          flexShrink: 0,
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '32px', height: '32px',
                background: 'rgba(0,255,136,0.1)',
                border: '1px solid rgba(0,255,136,0.3)',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Cpu size={16} color="var(--color-primary)" />
              </div>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>ElectrAI Diagnostics</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  electronics · circuits · components
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                id="mobile-sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  width: '34px', height: '34px',
                  background: 'rgba(0,255,136,0.1)',
                  border: '1px solid rgba(0,255,136,0.3)',
                  borderRadius: '8px',
                  display: 'none', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--color-primary)',
                }}
              >
                {sidebarOpen ? <CloseIcon size={18} /> : <Menu size={18} />}
              </button>
              <style>{`
                @media (max-width: 1024px) {
                  #mobile-sidebar-toggle { display: flex !important; }
                  .header-btn-group { display: none !important; }
                  .mobile-more-btn { display: flex !important; }
                }
              `}</style>
              
              <div className="header-btn-group" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {/* Upload image */}
                <label style={{
                  width: '34px', height: '34px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: 'var(--text-muted)',
                }}
                  title="Upload circuit image"
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,207,255,0.4)'; (e.currentTarget as HTMLElement).style.color = 'var(--color-secondary)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}
                >
                  <Upload size={16} style={{ animation: uploadLoading ? 'spin-slow 1s linear infinite' : 'none' }} />
                  <input type="file" style={{ display: 'none' }} accept="image/*" onChange={handleUpload} />
                </label>

                {/* Save session */}
                <button
                  onClick={async () => {
                    if (!user) {
                      const email = prompt('Enter your email to save this session:');
                      if (email) login(email);
                      return;
                    }
                    if (messages.length === 0) return;
                    const currentId = projectId || Date.now().toString();
                    const name = prompt('Name this diagnostic session:', messages[0]?.content?.slice(0, 40) || 'Electronics Diagnostic');
                    if (name) {
                      const finalId = await api.saveProject({ 
                        id: currentId, 
                        name, 
                        type: 'chat', // Use 'chat' type for diagnostic sessions
                        data: { messages },
                        vehicleContext: null // Explicitly electronics focus
                      });
                      setProjectId(finalId);
                      alert('Session saved to DRTKNG Vault!');
                    }
                  }}
                  disabled={messages.length === 0}
                  style={{
                    width: '34px', height: '34px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: messages.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: messages.length === 0 ? 0.4 : 1,
                    color: 'var(--text-muted)',
                    transition: 'all 0.2s ease',
                  }}
                  title="Save session"
                  onMouseEnter={e => { if (messages.length > 0) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,136,0.4)'; (e.currentTarget as HTMLElement).style.color = 'var(--color-primary)'; } }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}
                >
                  <Save size={16} />
                </button>

                {/* AR Mode */}
                <button
                  onClick={() => setShowAR(true)}
                  style={{
                    width: '34px', height: '34px',
                    background: 'rgba(0,255,136,0.05)',
                    border: '1px solid rgba(0,255,136,0.2)',
                    borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--color-primary)',
                    transition: 'all 0.2s ease',
                    animation: 'flicker 4s infinite'
                  }}
                  title="Launch AR Visualizer"
                >
                  <Zap size={16} />
                </button>

                {/* Clear */}
                <button
                  onClick={() => { if (confirm('Clear session?')) setMessages([]); }}
                  style={{
                    width: '34px', height: '34px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    transition: 'all 0.2s ease',
                  }}
                  title="Clear session"
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,68,102,0.4)'; (e.currentTarget as HTMLElement).style.color = 'var(--color-danger)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; }}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Mobile More Button */}
              <button
                className="mobile-more-btn"
                onClick={() => {
                   const action = prompt("Actions: (U)pload, (S)ave, (A)R Mode, (C)lear");
                   if (!action) return;
                   const cmd = action.toLowerCase();
                   if (cmd === 'u') document.getElementById('mobile-upload-input')?.click();
                   else if (cmd === 's') (document.querySelector('[title="Save session"]') as HTMLElement)?.click();
                   else if (cmd === 'a') setShowAR(true);
                   else if (cmd === 'c') (document.querySelector('[title="Clear session"]') as HTMLElement)?.click();
                }}
                style={{
                  width: '34px', height: '34px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  display: 'none', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                <ChevronRight size={18} />
                <input id="mobile-upload-input" type="file" style={{ display: 'none' }} accept="image/*" onChange={handleUpload} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>

            {/* Empty state */}
            {messages.length === 0 && !loading && (
              <div style={{ paddingTop: '1rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{
                    width: '64px', height: '64px',
                    background: 'rgba(0,255,136,0.08)',
                    border: '1px solid rgba(0,255,136,0.2)',
                    borderRadius: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.25rem',
                    animation: 'float 3s ease-in-out infinite',
                  }}>
                    <Activity size={28} color="var(--color-primary)" />
                  </div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                    ElectrAI Ready
                  </h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Describe your electronics problem or circuit issue.
                  </p>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>
                    QUICK START:
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.6rem' }}>
                    {quickStarters.map((starter) => (
                      <button
                        key={starter}
                        onClick={() => handleSend(starter)}
                        style={{
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '10px',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          color: 'var(--text-secondary)',
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,136,0.3)';
                          (e.currentTarget as HTMLElement).style.color = 'var(--color-primary)';
                          (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.05)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)';
                          (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                          (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)';
                        }}
                      >
                        {starter}
                        <ChevronRight size={13} style={{ flexShrink: 0, opacity: 0.5 }} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Message thread */}
            {messages.map((msg, i) => {
              const isLast = i === messages.length - 1;
              return (
              <div 
                key={i} 
                ref={isLast ? latestMessageRef : null}
                style={{ marginBottom: '1.5rem', animation: 'slide-up 0.3s ease forwards' }}
              >
                <MarkdownMessage message={msg} />
                {msg.role === 'assistant' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button
                      onClick={() => {
                        const lastUser = [...messages].reverse().find(m => m.role === 'user')?.content || 'Circuit schematic';
                        navigate('/schematics', { state: { initialQuery: lastUser } });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        background: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.25)',
                        borderRadius: '8px', padding: '0.35rem 0.75rem', fontSize: '0.75rem',
                        fontWeight: 600, color: 'var(--color-accent)', cursor: 'pointer', transition: 'all 0.2s'
                      }}
                    >
                      <BookOpen size={13} /> View Diagrams
                    </button>
                    <button
                      onClick={() => {
                        const lastUser = [...messages].reverse().find(m => m.role === 'user')?.content || 'Electronic part';
                        navigate('/components', { state: { initialQuery: lastUser } });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        background: 'rgba(0,207,255,0.08)', border: '1px solid rgba(0,207,255,0.25)',
                        borderRadius: '8px', padding: '0.35rem 0.75rem', fontSize: '0.75rem',
                        fontWeight: 600, color: 'var(--color-secondary)', cursor: 'pointer', transition: 'all 0.2s'
                      }}
                    >
                      <Layers size={13} /> Find Components
                    </button>
                    <button
                      onClick={() => {
                        const lastUser = [...messages].reverse().find(m => m.role === 'user')?.content || '';
                        navigate('/studio', { state: { initialQuery: lastUser } });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.25)',
                        borderRadius: '8px', padding: '0.35rem 0.75rem', fontSize: '0.75rem',
                        fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer', transition: 'all 0.2s'
                      }}
                    >
                      <Terminal size={13} /> Open in Studio
                    </button>
                  </div>
                )}
              </div>
            )})}

            {loading && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 1.25rem',
                  background: 'rgba(0,255,136,0.05)',
                  border: '1px solid rgba(0,255,136,0.15)',
                  borderRadius: '12px',
                  width: 'fit-content',
                }}>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    {[0, 150, 300].map(d => (
                      <div key={d} style={{
                        width: '8px', height: '8px',
                        background: 'var(--color-primary)',
                        borderRadius: '50%',
                        animation: 'pulse-green 1s ease-in-out infinite',
                        animationDelay: `${d}ms`,
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 500, fontFamily: 'var(--font-mono)' }}>
                    {loadingStatus || 'ElectrAI is thinking...'}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Toggle - Pop Out FAB */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'fixed', bottom: '2rem', right: '1.5rem',
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'var(--color-primary)', border: 'none',
            color: '#050912', boxShadow: '0 0 30px rgba(0,255,136,0.4)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          {sidebarOpen ? <CloseIcon size={24} /> : <Zap size={24} />}
        </button>
      )}

      {/* Expert Sidebar */}
      <div className={`diag-sidebar ${sidebarOpen ? 'open' : ''} pop-out-panel`} style={{
        position: isMobile ? 'fixed' : 'relative',
        top: 0, right: 0, bottom: 0,
        width: isMobile ? '85%' : '320px',
        borderLeft: '1px solid var(--border-color)',
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        overflowY: 'auto',
        background: isMobile ? 'rgba(5,9,18,0.98)' : 'rgba(5,9,18,0.4)',
        backdropFilter: isMobile ? 'blur(20px)' : 'none',
        zIndex: 500,
        height: '100dvh',
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isMobile && !sidebarOpen ? 'translateX(100%)' : 'translateX(0)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
          <Zap size={18} color="var(--color-primary)" />
          <h3 style={{ fontSize: '0.9rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Circuit Expert</h3>
        </div>

        {/* Digital Field Kit (Offline AI) - TEMPORARILY DISABLED FOR SIMPLICITY
        <div style={{ background: 'rgba(0,255,136,0.05)', borderRadius: '14px', padding: '1.25rem', border: '1px solid rgba(0,255,136,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
             <h4 style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 700, textTransform: 'uppercase' }}>Digital Field Kit</h4>
             {offlineStatus.loaded ? <Shield size={14} color="var(--color-primary)" /> : <Globe size={14} color="var(--text-muted)" />}
          </div>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.4 }}>
            {offlineStatus.loaded 
              ? 'Edge Intelligence active. You can chat even without a signal.' 
              : 'Download technical weights to enable offline diagnostics in low-signal areas.'}
          </p>
          
          {offlineStatus.progress > 0 && offlineStatus.progress < 100 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', marginBottom: '0.35rem' }}>
                <span style={{ color: 'var(--color-primary)' }}>{offlineStatus.message}</span>
                <span>{Math.round(offlineStatus.progress)}%</span>
              </div>
              <div style={{ height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${offlineStatus.progress}%`, background: 'var(--color-primary)', transition: 'width 0.3s ease' }} />
              </div>
            </div>
          )}

          {!offlineStatus.loaded && offlineStatus.progress === 0 && (
            <button 
              onClick={() => OfflineAI.getInstance().downloadModel()}
              style={{
                width: '100%', padding: '0.6rem', borderRadius: '8px',
                background: 'rgba(0,255,136,0.1)', border: '1px solid var(--color-primary)',
                color: 'var(--color-primary)', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer'
              }}
            >
              DOWNLOAD EDGE PACK
            </button>
          )}

          {offlineStatus.loaded && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-primary)', fontSize: '0.75rem', fontWeight: 800 }}>
               <Zap size={14} /> EDGE SYSTEM READY
            </div>
          )}
        </div>
        */}

        {/* Diagnostic Phase (moved into new block) */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', padding: '1.25rem', border: '1px solid var(--border-color)' }}>
          <h4 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.75rem', textTransform: 'uppercase' }}>Current Phase</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[
              { label: 'Initial Triage', status: messages.length === 0 ? 'active' : 'complete' },
              { label: 'Component Testing', status: messages.length > 0 && messages.length < 4 ? 'active' : messages.length >= 4 ? 'complete' : 'pending' },
              { label: 'Fault Isolation', status: messages.length >= 4 && messages.length < 8 ? 'active' : messages.length >= 8 ? 'complete' : 'pending' },
              { label: 'Repair Guidance', status: messages.length >= 8 ? 'active' : 'pending' },
            ].map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: p.status === 'pending' ? 0.4 : 1 }}>
                <div style={{
                  width: '18px', height: '18px',
                  borderRadius: '50%',
                  border: `2px solid ${p.status === 'active' ? 'var(--color-primary)' : p.status === 'complete' ? 'var(--color-primary)' : 'var(--text-muted)'}`,
                  background: p.status === 'complete' ? 'var(--color-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {p.status === 'complete' && <Zap size={10} color="#050912" />}
                  {p.status === 'active' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-primary)' }} />}
                </div>
                <span style={{ fontSize: '0.82rem', fontWeight: p.status === 'active' ? 700 : 500, color: p.status === 'active' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Expert Quirks / Tips */}
        <div style={{ background: 'linear-gradient(135deg, rgba(0,255,136,0.08), transparent)', borderRadius: '14px', padding: '1.25rem', border: '1px solid rgba(0,255,136,0.2)' }}>
          <h4 style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 700, marginBottom: '0.75rem', textTransform: 'uppercase' }}>Expert Pro Tip</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {messages.length === 0 ? "When measuring PCB trace voltages, ensure your multimeter's negative probe is truly on a solid ground plane to avoid ghost readings." : 
             messages.length < 4 ? "Always check for physical signs first: bulging electrolytics, browned resistors, or cracked solder joints (use a loupe!)" :
             "Logic levels matter. If you're interfacing 3.3V microcontrollers with 5V components, double-check that you're using bi-directional level shifters."}
          </p>
        </div>

        {/* Recommended Fixtures */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: '14px', padding: '1.25rem', border: '1px solid var(--border-color)' }}>
          <h4 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '0.75rem', textTransform: 'uppercase' }}>Fixture Tools</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {[
              { name: 'Multimeter', type: 'Essential' },
              { name: 'Oscilloscope', type: 'Advanced' },
              { name: 'ESR Meter', type: 'Caps' },
              { name: 'Logic Analyzer', type: 'Digital' },
              { name: 'Breadboard', type: 'Prototyping' },
            ].map(f => (
              <span key={f.name} style={{
                fontSize: '0.7rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '6px',
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
              }}>{f.name}</span>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', opacity: 0.6 }}>
          ElectrAI Engine v1.0.5<br />
          Quantum Diagnostics Enabled
        </div>
      </div>

      {/* AR Simulation Overlay */}
      {showAR && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(5,9,18,0.9)', backdropFilter: 'blur(10px)',
          display: 'flex', flexDirection: 'column'
        }}>
          {/* AR HUD Header */}
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid rgba(0,255,136,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--color-primary)', boxShadow: '0 0 10px var(--color-primary)', animation: 'pulse-green 1s infinite' }} />
              <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>AR HUD ACTIVE · SIMULATION MODE</span>
            </div>
            <button onClick={() => setShowAR(false)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: '#fff', padding: '0.5rem', borderRadius: '50%', cursor: 'pointer' }}>
              <CloseIcon size={20} />
            </button>
          </div>

          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
             {/* Crosshair */}
             <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '40px', height: '40px', pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', top: 0, left: '50%', width: '2px', height: '10px', background: 'var(--color-primary)' }} />
                <div style={{ position: 'absolute', bottom: 0, left: '50%', width: '2px', height: '10px', background: 'var(--color-primary)' }} />
                <div style={{ position: 'absolute', left: 0, top: '50%', width: '10px', height: '2px', background: 'var(--color-primary)' }} />
                <div style={{ position: 'absolute', right: 0, top: '50%', width: '10px', height: '2px', background: 'var(--color-primary)' }} />
             </div>

             {/* Mock Overlay markers */}
             <div style={{ position: 'absolute', top: '30%', left: '40%', padding: '0.5rem', border: '1px solid var(--color-primary)', borderRadius: '4px', background: 'rgba(0,255,136,0.1)', color: 'var(--color-primary)', fontSize: '0.7rem', fontWeight: 700 }}>
                MCU DETECTED: ESP32-WROOM-32
             </div>
             <div style={{ position: 'absolute', top: '60%', right: '35%', padding: '0.5rem', border: '1px solid var(--color-secondary)', borderRadius: '4px', background: 'rgba(0,207,255,0.1)', color: 'var(--color-secondary)', fontSize: '0.7rem', fontWeight: 700 }}>
                POWER RAIL: 3.3V (STABLE)
             </div>

             {/* Instructions Panel */}
             <div className="diagnose-ar-overlay" style={{ position: 'absolute', bottom: '2rem', left: '2rem', right: '2rem', maxWidth: '400px', background: 'rgba(5,9,18,0.88)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '12px', padding: '1.25rem', backdropFilter: 'blur(8px)', zIndex: 100 }}>
                <h4 style={{ color: 'var(--color-primary)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>AR REPAIR GUIDANCE</h4>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.5, color: '#fff' }}>
                   Point your camera at the circuit board. ElectrAI is mapping components and logic flows in real-time. 
                   <br/><br/>
                   1. Match the crosshair to the main MCU.
                   <br/>
                   2. Wait for thermal mapping (requires ElectrAI Thermal Probe).
                   <br/>
                   3. Follow highlighted traces to fault points.
                </p>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                   <span style={{ fontSize: '0.7rem', background: 'rgba(255,170,0,0.15)', color: 'var(--color-accent)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid rgba(255,170,0,0.3)' }}>SAFETY: UNPLUG DEVICE</span>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* Paywall */}
      {isLimitReached && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(5,9,18,0.85)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '1.5rem',
        }}>
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid rgba(0,255,136,0.3)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '420px',
            textAlign: 'center',
            boxShadow: '0 0 60px rgba(0,255,136,0.1)',
          }}>
            <div style={{
              width: '60px', height: '60px',
              background: 'rgba(0,255,136,0.1)',
              border: '1px solid rgba(0,255,136,0.3)',
              borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.25rem',
            }}>
              <Zap size={28} color="var(--color-primary)" />
            </div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.75rem' }}>Usage Limit Reached</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              You've used your free ElectrAI diagnostics. Sign in with Google for more free questions, or upgrade to Pro for unlimited access.
            </p>

            {/* Google Sign In */}
            <div style={{ marginBottom: '1rem' }}>
              <GoogleSignInButton width={300} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>

            <button
              onClick={() => navigate('/upgrade')}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #00ff88, #00cc6a)',
                color: '#050912',
                padding: '0.875rem',
                borderRadius: '10px',
                border: 'none',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 0 24px rgba(0,255,136,0.3)',
              }}
            >
              Upgrade to Pro — Unlimited Access
            </button>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div style={{
        background: 'rgba(5,9,18,0.9)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(0,255,136,0.12)',
        padding: '1rem 1.5rem',
        flexShrink: 0,
      }}>
        {/* Pre-Diagnosis Quick Intake Bar */}
        <div style={{ maxWidth: '900px', margin: '0 auto 0.6rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '0.2rem' }}>
            Fast Intake Tags:
          </span>
          {[
            { label: '+ ESP32', tag: '[MCU: ESP32] ' },
            { label: '+ Arduino Uno', tag: '[MCU: Arduino Uno] ' },
            { label: '+ RPi', tag: '[MCU: Raspberry Pi] ' },
            { label: '+ 5V USB', tag: '[Power: 5V USB] ' },
            { label: '+ 12V DC', tag: '[Power: 12V DC] ' },
            { label: '+ Overheating', tag: '[Symptom: Component Overheating] ' },
            { label: '+ Brownout', tag: '[Symptom: Brownout Reset] ' },
          ].map(chip => (
            <button
              key={chip.label}
              type="button"
              onClick={() => {
                if (!input.includes(chip.tag)) {
                  setInput(prev => chip.tag + prev);
                }
              }}
              style={{
                background: input.includes(chip.tag) ? 'rgba(0,255,136,0.15)' : 'var(--bg-surface)',
                border: input.includes(chip.tag) ? '1px solid rgba(0,255,136,0.4)' : '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '0.25rem 0.55rem',
                fontSize: '0.72rem',
                color: input.includes(chip.tag) ? 'var(--color-primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                transition: 'all 0.15s ease',
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              autoFocus
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder={isLimitReached ? 'Upgrade to continue...' : 'Describe your electronics issue...'}
              disabled={isLimitReached || loading}
              style={{
                width: '100%',
                background: 'var(--bg-surface-2)',
                border: '1px solid rgba(0,255,136,0.25)',
                borderRadius: '12px',
                padding: '0.875rem 3.5rem 0.875rem 1.25rem',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                outline: 'none',
                fontFamily: 'var(--font-body)',
                transition: 'all 0.2s ease',
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(0,255,136,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(0,255,136,0.08), 0 0 16px rgba(0,255,136,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(0,255,136,0.25)'; e.target.style.boxShadow = 'none'; }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading || isLimitReached}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: input.trim() && !loading && !isLimitReached
                  ? 'linear-gradient(135deg, #00ff88, #00cc6a)'
                  : 'var(--bg-surface)',
                border: 'none',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !input.trim() || loading || isLimitReached ? 'not-allowed' : 'pointer',
                opacity: !input.trim() || loading || isLimitReached ? 0.4 : 1,
                transition: 'all 0.2s ease',
                color: '#050912',
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
        <div style={{ maxWidth: '900px', margin: '0.4rem auto 0', textAlign: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            ElectrAI · electronics · Arduino · ESP32 · RPi · circuit repair
          </span>
        </div>
      </div>
    </div>
  );
}
